import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeService, setActiveService] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Add Schema.org JSON-LD
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "大家行政書士事務所",
      "image": "https://public.readdy.ai/ai/img_res/d0770232-3388-461a-b5ee-3765a5289f6e.png",
      "description": "東京都新宿区神楽坂の大家行政書士事務所。会社設立、建設業許可、在留資格申請など幅広い行政書士業務を提供。",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "水道町3-14 神楽坂Nビル 2-M",
        "addressLocality": "新宿区",
        "addressRegion": "東京都",
        "postalCode": "162-0811",
        "addressCountry": "JP"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "35.7089456",
        "longitude": "139.7281234"
      },
      "url": siteUrl,
      "telephone": "+81-90-4055-7489",
      "email": "manabu.oya35@gmail.com",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        }
      ],
      "priceRange": "¥¥",
      "areaServed": {
        "@type": "City",
        "name": "東京都"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      id: 1,
      title: '会社・法人関連業務',
      subtitle: '法人設立・補助金申請',
      description: '会社設立から補助金申請まで、企業のスタートアップを全面的にサポートいたします。',
      image: 'https://public.readdy.ai/ai/img_res/89ff9dd7c95385ed3ff29c4a59287b4f.jpg',
      bgColor: 'bg-orange-50',
      details: ['株式会社・合同会社設立', '定款作成・認証', '各種補助金申請サポート', '事業計画書作成支援']
    },
    {
      id: 2,
      title: '各種許認可業務',
      subtitle: '建設業・産廃・宅建・運送業',
      description: '建設業許可、産業廃棄物処理業、宅建業、運送業など各種許認可申請を代行いたします。',
      image: 'https://readdy.ai/api/search-image?query=construction%20site%20with%20safety%20helmet%20and%20official%20permit%20documents%20on%20clean%20desk%2C%20professional%20licensing%20paperwork%20with%20stamps%20and%20seals%2C%20bright%20office%20environment%20with%20white%20background%2C%20business%20documentation%20photography&width=400&height=500&seq=permit002&orientation=portrait',
      bgColor: 'bg-emerald-50',
      details: ['建設業許可申請', '産業廃棄物処理業許可', '宅地建物取引業免許', '一般貨物自動車運送事業許可']
    },
    {
      id: 3,
      title: '民事法務',
      subtitle: '契約書・遺言書・相続',
      description: '契約書作成から遺言書、相続手続きまで、個人の法的ニーズに対応いたします。',
      image: 'https://readdy.ai/api/search-image?query=elegant%20fountain%20pen%20on%20legal%20contract%20document%20with%20official%20seal%2C%20professional%20legal%20paperwork%20on%20clean%20white%20desk%2C%20soft%20natural%20lighting%2C%20minimalist%20business%20photography%20style%20with%20neutral%20background&width=400&height=500&seq=civil003&orientation=portrait',
      bgColor: 'bg-slate-50',
      details: ['各種契約書作成・リーガルチェック', '遺言書作成サポート', '相続手続き代行', '内容証明郵便作成']
    },
    {
      id: 4,
      title: '国際業務',
      subtitle: '在留資格・永住・帰化申請',
      description: '在留資格から永住、帰化申請まで、外国人の方の日本での生活をサポートいたします。',
      image: 'https://readdy.ai/api/search-image?query=japanese%20passport%20and%20visa%20documents%20with%20immigration%20stamps%20on%20clean%20white%20surface%2C%20international%20travel%20documentation%2C%20professional%20photography%20with%20soft%20lighting%20and%20minimal%20background%2C%20official%20government%20paperwork%20style&width=400&height=500&seq=visa004&orientation=portrait',
      bgColor: 'bg-amber-50',
      details: ['在留資格認定証明書交付申請', '在留期間更新・変更申請', '永住許可申請', '帰化許可申請']
    }
  ];

  const testimonials = [
    {
      name: '田中様',
      role: 'IT企業経営者',
      rating: 5,
      title: '迅速な対応に感謝',
      comment: '会社設立の手続きをお願いしましたが、スピーディーで丁寧な対応に大変満足しています。補助金申請のアドバイスも的確でした。',
      avatar: 'T'
    },
    {
      name: '李様',
      role: '外国人エンジニア',
      rating: 5,
      title: '安心してお任せできました',
      comment: '在留資格の変更申請でお世話になりました。複雑な手続きも分かりやすく説明していただき、無事に許可が下りました。',
      avatar: 'L'
    },
    {
      name: '佐藤様',
      role: '建設会社代表',
      rating: 5,
      title: '専門知識が豊富',
      comment: '建設業許可の更新をお願いしました。法改正にも詳しく、的確なアドバイスをいただけて助かりました。',
      avatar: 'S'
    },
    {
      name: '山田様',
      role: '個人事業主',
      rating: 5,
      title: '親身な相談対応',
      comment: '契約書のリーガルチェックをお願いしました。細かい点まで丁寧に確認していただき、安心して契約を結ぶことができました。',
      avatar: 'Y'
    },
    {
      name: 'Chen様',
      role: '留学生',
      rating: 5,
      title: '就労ビザ取得成功',
      comment: '留学ビザから就労ビザへの変更をサポートしていただきました。書類準備から面接対策まで、手厚いサポートに感謝しています。',
      avatar: 'C'
    },
    {
      name: '鈴木様',
      role: '相続人',
      rating: 5,
      title: '相続手続きがスムーズ',
      comment: '父の相続手続きでお世話になりました。複雑な手続きも一つ一つ丁寧に説明していただき、無事に完了することができました。',
      avatar: 'S'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/90 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/d0770232-3388-461a-b5ee-3765a5289f6e.png" alt="大家行政書士事務所" className="h-12 w-auto" />
              <span className="text-xl font-bold text-slate-900">大家行政書士事務所</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="text-sm text-slate-700 hover:text-slate-900 whitespace-nowrap transition-colors">サービス案内</a>
              <a href="#about" className="text-sm text-slate-700 hover:text-slate-900 whitespace-nowrap transition-colors">事務所紹介</a>
              <a href="#contact" className="text-sm text-slate-700 hover:text-slate-900 whitespace-nowrap transition-colors">お問い合わせ</a>
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:09040557489" className="hidden md:flex items-center gap-2 text-sm text-slate-700 hover:text-slate-900 whitespace-nowrap transition-colors">
                <i className="ri-phone-line"></i>
                <span>090-4055-7489</span>
              </a>
              <a href="#contact" className="bg-slate-900 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-slate-800 transition-colors whitespace-nowrap flex items-center gap-2">
                無料相談予約
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://readdy.ai/api/search-image?query=panoramic%20view%20of%20tokyo%20shinjuku%20skyline%20with%20modern%20skyscrapers%20and%20kagurazaka%20neighborhood%20streets%20at%20golden%20hour%2C%20warm%20sunset%20lighting%20over%20urban%20cityscape%2C%20professional%20architectural%20photography%20with%20soft%20focus%20background%20and%20atmospheric%20depth%2C%20clean%20composition&width=1920&height=1080&seq=hero001&orientation=landscape" 
            alt="東京新宿の街並み" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/30 to-slate-900/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              <span className="hidden md:block">
                あなたのビジネスと<br />
                暮らしを法的に<br />
                サポートするパートナー
              </span>
              <span className="block md:hidden">
                あなたのビジネスと暮らしを<br />
                法的にサポートするパートナー
              </span>
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              会社設立から在留資格まで、東京・新宿で信頼される法務パートナー
            </p>
            <a href="#services" className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full text-base font-medium hover:bg-slate-100 transition-colors whitespace-nowrap">
              サービスを見る
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <p className="text-sm text-slate-500 mb-3 tracking-wider">SERVICES</p>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              私たちができること
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <div 
                key={service.id}
                className="group cursor-pointer"
                onMouseEnter={() => setActiveService(service.id)}
                onMouseLeave={() => setActiveService(null)}
              >
                <div className={`${service.bgColor} rounded-3xl overflow-hidden transition-all duration-300 ${activeService === service.id ? 'shadow-2xl scale-105' : 'shadow-lg'}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="bg-white p-6">
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{service.title}</h3>
                    <p className="text-sm text-slate-600">{service.subtitle}</p>
                  </div>
                </div>
                {activeService === service.id && (
                  <div className="mt-4 p-6 bg-slate-50 rounded-2xl">
                    <p className="text-sm text-slate-700 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <i className="ri-check-line text-emerald-600 mt-0.5"></i>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block border border-slate-300 px-4 py-1.5 rounded-full text-xs text-slate-600 mb-6">
                ABOUT US
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="text-slate-900">信頼と実績で</span><br />
                <span className="text-slate-600 font-normal">あなたの未来を支える</span>
              </h2>
              <div className="h-8"></div>
              <p className="text-base text-slate-700 leading-relaxed mb-8">
                大家行政書士事務所は、東京・新宿の神楽坂に拠点を置き、企業法務から個人の民事法務、国際業務まで幅広くサポートしています。代表の大家学は、東京出入国在留管理局の申請取次行政書士として、お客様一人ひとりに寄り添った丁寧なサービスを提供しています。
              </p>
              <a href="#contact" className="inline-flex items-center text-slate-900 font-medium hover:gap-3 transition-all underline">
                詳しく見る
              </a>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20japanese%20office%20building%20exterior%20in%20shinjuku%20tokyo%20with%20clean%20architecture%20and%20glass%20windows%2C%20professional%20business%20district%20atmosphere%2C%20bright%20daylight%20with%20clear%20sky%2C%20contemporary%20urban%20commercial%20building%20facade&width=400&height=400&seq=office-building-001&orientation=squarish" 
                    alt="オフィスビル外観"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="aspect-square rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=organized%20legal%20documents%20and%20official%20paperwork%20on%20clean%20white%20desk%20with%20laptop%20computer%2C%20professional%20administrative%20office%20workspace%2C%20natural%20lighting%2C%20minimalist%20business%20environment%20with%20neutral%20tones%20and%20modern%20aesthetic&width=400&height=400&seq=office-desk-002&orientation=squarish" 
                    alt="事務所の書類"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-amber-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-slate-400"></div>
              <p className="text-sm text-slate-600 tracking-wider">TESTIMONIALS</p>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              お客様からの信頼の声
            </h2>
            <p className="text-base text-slate-600">
              実際にご利用いただいたお客様の声をご紹介します
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-500 text-lg"></i>
                  ))}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{testimonial.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">{testimonial.comment}</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-bold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-sm">{testimonial.name}</div>
                    <div className="text-xs text-slate-600">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://readdy.ai/api/search-image?query=panoramic%20tokyo%20night%20cityscape%20with%20illuminated%20skyscrapers%20and%20kagurazaka%20evening%20streets%2C%20warm%20golden%20hour%20lighting%20over%20urban%20landscape%2C%20cinematic%20wide%20angle%20photography%20with%20atmospheric%20depth%20and%20soft%20bokeh%20background%2C%20professional%20architectural%20shot&width=1920&height=800&seq=cta001&orientation=landscape" 
            alt="東京の夜景"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 via-slate-900/50 to-slate-900/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                まずは無料相談から<br />
                はじめませんか
              </h2>
              <a href="#contact" className="inline-flex items-center gap-4 bg-white text-slate-900 px-8 py-4 rounded-full text-base font-medium hover:bg-slate-100 transition-colors whitespace-nowrap">
                今すぐ相談予約
                <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white"></i>
                </div>
              </a>
            </div>
            <div className="text-right">
              <p className="text-xl text-white leading-relaxed">
                お電話またはメールで<br />
                お気軽にお問い合わせください
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">お問い合わせ</h2>
            <p className="text-base text-slate-600">
              ご相談・ご質問はお気軽にお問い合わせください
            </p>
          </div>
          <form 
            id="contact-form"
            data-readdy-form
            action="https://readdy.ai/api/form/d5a8o06fmrgp9cvluu00"
            method="POST"
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              // Validation
              const name = formData.get('name') as string;
              const email = formData.get('email') as string;
              const phone = formData.get('phone') as string;
              const service = formData.get('service') as string;
              const message = formData.get('message') as string;

              if (!name || !email || !phone || !service || !message) {
                alert('すべての項目を入力してください');
                return;
              }

              if (message.length > 500) {
                alert('お問い合わせ内容は500文字以内で入力してください');
                return;
              }

              // Submit
              fetch(form.action, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams(formData as any).toString()
              })
              .then(response => {
                if (response.ok) {
                  alert('お問い合わせを受け付けました。担当者より折り返しご連絡いたします。');
                  form.reset();
                } else {
                  alert('送信に失敗しました。もう一度お試しください。');
                }
              })
              .catch(() => {
                alert('送信に失敗しました。もう一度お試しください。');
              });
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="山田太郎"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all text-sm"
                  placeholder="example@email.com"
                />
              </div>
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                電話番号 <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all text-sm"
                placeholder="090-1234-5678"
              />
            </div>
            <div>
              <label htmlFor="service" className="block text-sm font-medium text-slate-700 mb-2">
                ご相談内容 <span className="text-red-500">*</span>
              </label>
              <select
                id="service"
                name="service"
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all text-sm"
              >
                <option value="">選択してください</option>
                <option value="会社・法人関連業務">会社・法人関連業務</option>
                <option value="各種許認可業務">各種許認可業務</option>
                <option value="民事法務">民事法務</option>
                <option value="国際業務">国際業務</option>
                <option value="その他">その他</option>
              </select>
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                お問い合わせ内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent outline-none transition-all resize-none text-sm"
                placeholder="ご相談内容を詳しくお書きください（500文字以内）"
              ></textarea>
              <p className="text-xs text-slate-500 mt-1">※500文字以内で入力してください</p>
            </div>
            <button
              type="submit"
              className="w-full bg-slate-900 text-white py-4 rounded-lg font-medium hover:bg-slate-800 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              送信する
              <i className="ri-send-plane-fill"></i>
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/d0770232-3388-461a-b5ee-3765a5289f6e.png" alt="大家行政書士事務所" className="h-10 w-auto brightness-0 invert" />
              </div>
              <p className="text-sm text-slate-400">
                東京・新宿で信頼される法務パートナー
              </p>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-base">サービス</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#services" className="hover:text-white transition-colors">会社法務</a></li>
                <li><a href="#services" className="hover:text-white transition-colors">許認可</a></li>
                <li><a href="#services" className="hover:text-white transition-colors">民事法務</a></li>
                <li><a href="#services" className="hover:text-white transition-colors">国際業務</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-base">事務所案内</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#about" className="hover:text-white transition-colors">代表挨拶</a></li>
                <li><Link to="/access" className="hover:text-white transition-colors">アクセス</Link></li>
                <li><a href="#contact" className="hover:text-white transition-colors">料金表</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">よくある質問</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-base">お問い合わせ</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#contact" className="hover:text-white transition-colors">無料相談</a></li>
                <li><a href="tel:09040557489" className="hover:text-white transition-colors">電話相談</a></li>
                <li><a href="mailto:manabu.oya35@gmail.com" className="hover:text-white transition-colors">メール相談</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">予約フォーム</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <div className="rounded-2xl overflow-hidden h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=bright%20modern%20japanese%20administrative%20law%20office%20interior%20with%20organized%20desk%20workspace%2C%20professional%20legal%20documents%20and%20books%20on%20shelves%2C%20natural%20daylight%20streaming%20through%20windows%2C%20clean%20minimalist%20design%20with%20warm%20wood%20tones%20and%20neutral%20colors%2C%20welcoming%20professional%20atmosphere&width=400&height=300&seq=footer002&orientation=landscape" 
                  alt="事務所"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-lg font-serif leading-relaxed">
                  あなたの法務パートナー<br />
                  大家行政書士事務所<br />
                  <span className="text-sm text-slate-400">〒162-0811 東京都新宿区水道町3-14 神楽坂Nビル 2-M</span><br />
                  <span className="text-sm text-slate-400">manabu.oya35@gmail.com</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-400 mb-4">
                  最新情報をお届けします
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="メールアドレス"
                    className="flex-1 px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                  <button className="bg-amber-500 text-slate-900 px-6 py-2.5 rounded-lg font-medium hover:bg-amber-400 transition-colors whitespace-nowrap flex items-center justify-center w-12 h-12">
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-400">
              © 大家行政書士事務所 2025 All rights reserved
            </p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-white transition-colors">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
